import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

// Components
import DraggableTiles from './DraggableTiles';
import ItemPreviews from './ItemPreviews';
/* eslint no-unused-vars:0 */
import ListView from '../List/ListView';
import ListRow from '../List/ListRow';

// Helpers
import actions from '../../actions/FavoritesActions';
import { viewModes, trackingLists, tabs } from '../../constants';
import classnames from 'classnames';
import get from 'lodash.get';
import { getCurrency } from '../../../utils/currencyHelpers';

// Styles
import styles from './styles/Tile.scss';

// Tracking
import { trackImpressions } from '../../helpers/trackingHelpers';
import trackingHelpers from '../../helpers/trackingHelpers';

let impressionIdsFired = {};
let impressionItemsToFire = [];

class Tiles extends React.Component {
    constructor(props) {
        super(props);
        impressionIdsFired = {};

        this.state = { currency: 'USD' };

        this.addNote = this.addNote.bind(this);
        this.handleCurrency = this.handleCurrency.bind(this);
        this.dropHandler = this.dropHandler.bind(this);
        this.getItems = this.getItems.bind(this);
        this.updateSelectAllStatus = this.updateSelectAllStatus.bind(this);
    }

    componentDidMount() {
        this.handleCurrency();
    }

    componentWillReceiveProps(nextProps) {
        this.updateSelectAllStatus(nextProps);
    }

    componentDidUpdate() {
        this.handleCurrency();
    }

    getItems(portfolioItemsConnection) {
        const edges = get(portfolioItemsConnection, 'edges') || [];
        return edges.map(edge => {
            return {
                ...edge.node.item,
                note: edge.node.note,
                portfolioItemId: edge.node.portfolioItemId,
            };
        });
    }

    handleCurrency() {
        const currency = getCurrency({
            userCurrencyPreference: get(this.props, 'user.profile.currency') || 'USD',
        });
        if (this.state.currency !== currency) {
            this.props.setCurrency(currency);
        }
    }

    updateSelectAllStatus(nextProps) {
        const items = this.getItems(nextProps.portfolioItemsConnection);
        const { selectItems, selectAllStatus } = nextProps;
        if (selectAllStatus === 'select' && this.props.selectAllStatus !== 'select') {
            const itemIds = items.map(item => {
                return {
                    itemId: item.serviceId,
                    portfolioItemId: item.portfolioItemId,
                    src: item.firstPhotoWebPath,
                    title: item.title,
                };
            });
            selectItems(itemIds);
        } else if (selectAllStatus === 'deselect' && this.props.selectAllStatus !== 'deselect') {
            selectItems([]);
        }
    }

    addNote(item) {
        const note = item.note || '';
        const src = item.firstPhotoWebPath || '';
        const title = item.title;
        this.props.selectItems([{ itemId: item.serviceId, portfolioItemId: item.portfolioItemId }]);
        this.props.setNoteImage(src);
        this.props.setNoteTitle(title);
        this.props.setNote(note);
        this.props.addNote();
    }

    dropHandler(draggedFromIndex, draggedToIndex) {
        // Get set of items prior to dragging
        const items = this.props.portfolioItemsConnection.edges;
        // Get dragged item's portfolioItemId
        const targetItemId = get(items[draggedFromIndex], 'node.portfolioItemId');
        let startItemId;
        let endItemId;
        // Find the adjacent items and reorder the items
        if (draggedFromIndex < draggedToIndex) {
            // dragged item was moved to a higher position
            // If item was moved to 0th position, there is no item to it's left (no startItemId), otherwise startItemId
            // is the portfolioItemId of the item at one index less than the index the item was dragged to (draggedToIndex)
            startItemId =
                draggedToIndex <= 0 ? null : get(items[draggedToIndex], 'node.portfolioItemId');
            // If item was moved to last position, there is no item to it's right (no endItemId), otherwise endItemId
            // is the portfolioItemId of the item at one index greater than the index the item was dragged to (draggedToIndex)
            endItemId =
                draggedToIndex >= items.length - 1
                    ? null
                    : get(items[draggedToIndex + 1], 'node.portfolioItemId');
        } else {
            // dragged item was moved to a lower position
            // If item was moved to 0th position, there is no item to it's left (no startItemId), otherwise startItemId
            // is the portfolioItemId of the item at one index less than the index the item was dragged to (draggedToIndex)
            startItemId =
                draggedToIndex <= 0 ? null : get(items[draggedToIndex - 1], 'node.portfolioItemId');
            // If item was moved to last position, there is no item to it's right (no endItemId), otherwise endItemId
            // is the portfolioItemId of the item at one index greater than the index the item was dragged to (draggedToIndex)
            endItemId =
                draggedToIndex >= items.length - 1
                    ? null
                    : get(items[draggedToIndex], 'node.portfolioItemId');
        }
        // Reorder the items via a relay mutation
        this.props.setReordered(true);
        this.props.onDrop({
            action: 'REORDER',
            targetPortfolioItemId: targetItemId,
            startPortfolioItemId: startItemId,
            endPortfolioItemId: endItemId,
        });
    }

    render() {
        const {
            currency,
            reordered,
            reordering,
            selecting,
            user,
            isPublic,
            showDealerBranding,
            showProjects,
            type,
            viewMode,
            viewer,
        } = this.props;
        const itemEdges = get(this.props, 'portfolioItemsConnection.edges') || [];

        // FINDING-10858
        // Need to filter out null items in the event that a favorited item is not in SOLR
        const items = itemEdges.reduce((_items, edge) => {
            if (get(edge, 'node.item')) {
                _items.push({
                    ...edge.node.item,
                    note: edge.node.note,
                    portfolioItemId: edge.node.portfolioItemId,
                });
            }
            return _items;
        }, []);
        const tileClasses = classnames(styles.tile, {
            [styles.boxShadows]: reordering || selecting,
            [styles.boxShadow]: !reordering && !selecting && !isPublic,
        });

        if (reordering) {
            const draggableItems = items.map((item, index) => {
                return { src: item.firstPhotoWebPath, marker: index, sort: index };
            });

            return (
                <div className={styles.dragContainer}>
                    <DraggableTiles
                        reordered={reordered}
                        onDrop={this.dropHandler}
                        draggableItems={draggableItems}
                        selecting={selecting}
                    />
                </div>
            );
        }

        const itemProps = {
            addNote: this.addNote,
            currency,
            isPublic,
            showProjects,
            selecting,
            showDealerBranding,
            user,
            viewer,
        };
        let content = null;

        if (viewMode === viewModes.GRID) {
            content = items.map((item, index) => {
                if (!impressionIdsFired[item.serviceId]) {
                    impressionIdsFired[item.serviceId] = true;
                    impressionItemsToFire.push(Object.assign({ index: index + 1 }, item));
                }
                return (
                    <ItemPreviews
                        key={item.firstPhotoWebPath}
                        tileClasses={tileClasses}
                        index={index}
                        item={item}
                        note={item.note}
                        portfolioItemId={item.portfolioItemId}
                        projects={item.tilesProjects}
                        totalItems={items.length}
                        {...itemProps}
                    />
                );
            });
            if (impressionItemsToFire.length) {
                if (type === tabs.ITEMS) {
                    trackImpressions(impressionItemsToFire, trackingLists.ITEMS);
                } else if (type === tabs.FOLDER) {
                    trackImpressions(impressionItemsToFire, trackingLists.FOLDER);
                }

                impressionItemsToFire = [];
            }
        } else {
            content = <ListView items={items} {...itemProps} />;
        }

        return <div className={`rowFlex ${styles.tileContainer}`}>{content}</div>;
    }
}

Tiles.defaultProps = {
    isPublic: false,
};

Tiles.propTypes = {
    addNote: PropTypes.func,
    currency: PropTypes.string,
    onDrop: PropTypes.func,
    portfolioItemsConnection: PropTypes.object,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    reordering: PropTypes.bool,
    selectAllStatus: PropTypes.string,
    selecting: PropTypes.bool,
    selectItems: PropTypes.func,
    setCurrency: PropTypes.func,
    setNote: PropTypes.func,
    setNoteImage: PropTypes.func,
    setNoteTitle: PropTypes.func,
    setReordered: PropTypes.func,
    showDealerBranding: PropTypes.bool,
    showProjects: PropTypes.bool,
    type: PropTypes.string,
    user: PropTypes.object,
    isPublic: PropTypes.bool,
    viewer: PropTypes.object,
    viewMode: PropTypes.string,
};

const mapStateToProps = state => {
    return {
        currency: state.favorites.uiState.currency,
        reordering: state.favorites.uiState.reordering,
        selectAllStatus: state.favorites.uiState.selectAllStatus,
        selecting: state.favorites.uiState.selecting,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setCurrency: currency => {
            dispatch(actions.setCurrency({ currency }));
        },
        setNote: note => {
            dispatch(actions.setNote({ note }));
        },
        setNoteImage: noteImage => {
            dispatch(actions.setNoteImage({ noteImage }));
        },
        setNoteTitle: noteTitle => {
            dispatch(actions.setNoteTitle({ noteTitle }));
        },
        setReordered: reordered => {
            dispatch(actions.setReordered({ reordered }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(Tiles);
Tiles = createFragmentContainer(ReduxWrapped, {
    viewer: graphql`
        fragment Tiles_viewer on Viewer {
            ...ItemPreviews_viewer
        }
    `,
    portfolioItemsConnection: graphql`
        fragment Tiles_portfolioItemsConnection on PortfolioItemsConnection {
            edges {
                node {
                    portfolioItemId
                    note
                    item {
                        id
                        serviceId
                        title
                        firstPhotoWebPath(size: large)
                        ...trackingHelpers_item @relay(mask: false)
                        tilesProjects: projects(userId: $userId) @include(if: $hasUserId) {
                            name
                            serviceId
                        }
                        ...ListRow_item
                        ...ItemPreviews_item
                    }
                }
            }
        }
    `,
});

export default Tiles;
